import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { MessageSquarePlus, Pencil, Trash2, Save, X, MessageCircle } from "lucide-react";
import { toast } from "sonner";

function timeLabel(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function BeneficiaireComments({ beneficiaryId }) {
  const { user } = useAuth();
  const canWrite = ["admin", "manager", "gestionnaire"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");

  const load = async () => {
    if (!beneficiaryId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/beneficiaries/${beneficiaryId}/comments`);
      setItems(data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beneficiaryId]);

  const add = async () => {
    const t = text.trim();
    if (!t) {
      toast.error("Le commentaire ne peut pas être vide");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/beneficiaries/${beneficiaryId}/comments`, { text: t });
      setText("");
      toast.success("Commentaire ajouté");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditingText(c.text);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  const saveEdit = async () => {
    const t = editingText.trim();
    if (!t) {
      toast.error("Texte requis");
      return;
    }
    try {
      await api.put(`/beneficiaries/${beneficiaryId}/comments/${editingId}`, { text: t });
      toast.success("Commentaire modifié");
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (c) => {
    if (!window.confirm("Supprimer ce commentaire ?")) return;
    try {
      await api.delete(`/beneficiaries/${beneficiaryId}/comments/${c.id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="space-y-4" data-testid="beneficiaire-comments">
      {canWrite && (
        <div className="bg-white border border-zinc-200 rounded-sm p-4">
          <label className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 flex items-center gap-1.5 mb-2">
            <MessageSquarePlus size={12} /> Ajouter un commentaire ou une précision
          </label>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            maxLength={5000}
            placeholder="Notes de gestion, précisions, historique de contact, points de vigilance…"
            data-testid="ben-comment-textarea"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] font-mono text-zinc-400">{text.length}/5000</span>
            <Button
              onClick={add}
              disabled={saving || !text.trim()}
              className="bg-[#0055FF] hover:bg-[#0044CC]"
              size="sm"
              data-testid="ben-comment-add-btn"
            >
              <MessageSquarePlus size={13} className="mr-1" />
              {saving ? "Envoi…" : "Ajouter"}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center text-sm text-zinc-500 font-mono">
          Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center text-sm text-zinc-500 font-mono">
          Aucun commentaire pour l&apos;instant.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="ben-comments-list">
          {items.map((c) => {
            const isOwn = c.created_by_id === user?.id;
            const canEdit = canWrite && (isOwn || user?.role === "admin");
            const isEditing = editingId === c.id;
            return (
              <li
                key={c.id}
                className="bg-white border border-zinc-200 rounded-sm p-4"
                data-testid={`ben-comment-${c.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="h-6 w-6 rounded-sm bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center flex-shrink-0">
                        <MessageCircle size={11} />
                      </span>
                      <span className="text-xs font-semibold text-zinc-800">
                        {c.created_by_name || "Utilisateur"}
                      </span>
                      <span className="text-[10px] font-mono text-zinc-500">
                        {timeLabel(c.created_at)}
                        {c.updated_at ? ` · modifié ${timeLabel(c.updated_at)}` : ""}
                      </span>
                    </div>
                    {isEditing ? (
                      <>
                        <Textarea
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          rows={3}
                          maxLength={5000}
                          data-testid={`ben-comment-edit-textarea-${c.id}`}
                        />
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            size="sm"
                            onClick={saveEdit}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`ben-comment-save-${c.id}`}
                          >
                            <Save size={12} className="mr-1" /> Enregistrer
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={cancelEdit}
                            data-testid={`ben-comment-cancel-${c.id}`}
                          >
                            <X size={12} className="mr-1" /> Annuler
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-zinc-700 whitespace-pre-wrap break-words">
                        {c.text}
                      </p>
                    )}
                  </div>
                  {canEdit && !isEditing && (
                    <div className="flex-shrink-0 flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={() => startEdit(c)}
                        title="Modifier"
                        data-testid={`ben-comment-edit-${c.id}`}
                      >
                        <Pencil size={12} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-red-600"
                        onClick={() => remove(c)}
                        title="Supprimer"
                        data-testid={`ben-comment-delete-${c.id}`}
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
